import { Navigate, useLocation, useSearchParams } from "react-router-dom"
import { SubmissionPackPage } from "@/components/pack/SubmissionPackPage"
import { SubmissionFilesSection } from "@/components/pack/SubmissionFilesSection"
import { BidSubNav } from "@/components/bid-menu/BidSubNav"
import { SUBMIT_VIEWS, SUB_VIEW_PARAM, parseSubView, submitViewRedirectTarget } from "@/lib/bid-sections"
import { BidLifecycleActionsRow } from "@/components/bid-lifecycle/BidLifecycleActionsRow"
import { bidLifecycleOf, isOpenLifecycle } from "@/lib/bid-lifecycle"
import { useBidsStore } from "@/stores/bids-store"

interface SubmitSectionProps {
  bidId: string
  readOnly: boolean
}

/**
 * Submit: the lifecycle actions, the exact files each checklist item will send
 * (with their checks and, after Mark as submitted, the frozen lodged copies),
 * then the pack itself.
 */
export function SubmitSection({ bidId, readOnly }: SubmitSectionProps) {
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const view = parseSubView(SUBMIT_VIEWS, searchParams.get(SUB_VIEW_PARAM))
  const target = submitViewRedirectTarget(bidId, location.search)
  const bid = useBidsStore((s) => (s.selectedBid?.id === bidId ? s.selectedBid : null))
  // Reopened after lodging (lodged → Active or Lead): the lodged copies are history.
  const lodgedIsHistory = !!bid && isOpenLifecycle(bidLifecycleOf(bid).lifecycle)

  if (target) return <Navigate replace to={{ ...target, hash: location.hash }} />

  return (
    <div className="flex h-full flex-col">
      <BidSubNav label="Submit" views={SUBMIT_VIEWS} active={view} />
      <div className="min-h-0 flex-1 overflow-auto px-6 py-4">
        {bid && <BidLifecycleActionsRow bid={bid} readOnly={readOnly} className="mb-4" />}
        <div className="mb-6">
          <SubmissionFilesSection bidId={bidId} readOnly={readOnly} lodgedIsHistory={lodgedIsHistory} />
        </div>
        <SubmissionPackPage bidId={bidId} readOnly={readOnly} />
      </div>
    </div>
  )
}
